import React, { useEffect } from "react";
import { Select, Tooltip } from "antd";
import { EnvironmentOutlined } from "@ant-design/icons";
import { useCustom } from "@refinedev/core";
import { useSiteContext, SiteInfo } from "../contexts/SiteContext";
import { useTranslation } from "react-i18next";

interface SiteSelectorProps {
  /** Compact mode for mobile */
  compact?: boolean;
}

export const SiteSelector: React.FC<SiteSelectorProps> = ({ compact }) => {
  const { t } = useTranslation();
  const {
    isEnabled,
    selectedSiteId,
    setSelectedSiteId,
    userSite,
    setUserSite,
    availableSites,
    setAvailableSites,
    canSelectSite,
  } = useSiteContext();

  // Fetch user's site info
  const { data: userSiteData, isLoading: isLoadingUserSite } =
    useCustom<SiteInfo>({
      url: "users/me/site",
      method: "get",
      queryOptions: {
        enabled: isEnabled,
      },
    });

  // Fetch all available sites
  const { data: sitesData, isLoading: isLoadingSites } = useCustom<SiteInfo[]>({
    url: "users/sites",
    method: "get",
    queryOptions: {
      enabled: isEnabled,
    },
  });

  // Update context when data is loaded
  useEffect(() => {
    if (userSiteData?.data) {
      setUserSite(userSiteData.data);
    }
  }, [userSiteData, setUserSite]);

  useEffect(() => {
    if (sitesData?.data) {
      setAvailableSites(sitesData.data);
    }
  }, [sitesData, setAvailableSites]);

  // Don't render if feature is disabled
  if (!isEnabled) {
    return null;
  }

  // Don't render if user has fixed site (non-admin/BOD)
  if (!canSelectSite) {
    // Show user's assigned site as a badge/tooltip
    const displayName = userSite?.description || userSite?.siteName;
    if (displayName) {
      return (
        <Tooltip title={t("site.yourSite", { site: displayName })}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              color: "#1890ff",
              fontSize: 12,
            }}
          >
            <EnvironmentOutlined />
            {!compact && displayName}
          </span>
        </Tooltip>
      );
    }
    return null;
  }

  const isLoading = isLoadingUserSite || isLoadingSites;

  // Handle site change - update sessionStorage and reload page to refetch all data
  const handleSiteChange = (value: string) => {
    const newSiteId = value || null;
    setSelectedSiteId(newSiteId);

    // Reload the page to ensure all data is refetched with the new site filter
    window.location.reload();
  };

  // Build options for Select - prefer description over siteName for display
  const options = [
    { value: "", label: t("site.allSites", "All Sites") },
    ...availableSites.map((site) => ({
      value: site.siteId,
      label: site.description || site.siteName,
    })),
  ];

  return (
    <Select
      placeholder={compact ? t("site.site", "Site") : t("site.selectSite", "Select Site")}
      value={selectedSiteId || ""}
      onChange={handleSiteChange}
      options={options}
      loading={isLoading}
      style={{
        width: compact ? 100 : 160,
        fontSize: compact ? 12 : 13,
      }}
      size="small"
      allowClear={false}
      suffixIcon={<EnvironmentOutlined />}
      popupMatchSelectWidth={false}
    />
  );
};
